import * as React from 'react';
import { cn } from '@/lib/utils.ts';
import {
  columnLabelClass,
  screenTitleClass,
  sectionLabelClass,
} from '@/components/ui/typography.ts';

/**
 * Nazwa ekranu — 24 px / 800. Ta sama skala, którą `Table` rysuje swój tytuł,
 * więc lista wyjazdów i ekran z podzakładkami mają jeden nagłówek, nie dwa
 * różne. Wcześniej strony przepisywały z ręki 32 px / 600.
 */
const PageTitle = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLHeadingElement>) => (
  <h1
    className={cn(
      'text-2xl font-extrabold tracking-[-0.01em] text-base-text',
      className,
    )}
    {...props}
  />
);
PageTitle.displayName = 'PageTitle';

const SubpageTitle = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLHeadingElement>) => (
  <h1
    className={cn('text-[24px] font-semibold tracking-tight', className)}
    {...props}
  />
);
SubpageTitle.displayName = 'SubpageTitle';

/**
 * Nazwa sekcji wewnątrz ekranu — 15,5 px / 700, celowo blisko treści.
 * Wcześniejsze 20 px dominowało nad danymi, które sekcja opisuje.
 */
const SectionTitle = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLHeadingElement>) => (
  <h2
    className={cn(
      'text-[15.5px] font-bold tracking-[-0.005em] text-base-text',
      className,
    )}
    {...props}
  />
);
SectionTitle.displayName = 'SectionTitle';

const SubsectionTitle = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLHeadingElement>) => (
  <h3
    className={cn('text-lg font-semibold text-base-text', className)}
    {...props}
  />
);
SubsectionTitle.displayName = 'SubsectionTitle';

const SmailTitle = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLHeadingElement>) => (
  <h3
    className={cn('text-base font-semibold text-base-text', className)}
    {...props}
  />
);
SmailTitle.displayName = 'SmailTitle';

const Description = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLHeadingElement>) => (
  <h3 className={cn('text-sm text-neutral-500', className)} {...props} />
);
Description.displayName = 'Description';

/** Tytuł ekranu rekordu — uczestnik, klient, rezerwacja, krok przepływu. */
const ScreenTitle = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLHeadingElement>) => (
  <h2 className={cn(screenTitleClass, className)} {...props} />
);
ScreenTitle.displayName = 'ScreenTitle';

/** Wersalikowa etykieta paska sekcji — ciemniejsza od nagłówka kolumny. */
const SectionLabel = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLSpanElement>) => (
  <span className={cn(sectionLabelClass, className)} {...props} />
);
SectionLabel.displayName = 'SectionLabel';

/** Wersalikowa etykieta nagłówka kolumny, tytułu karty i panelu. */
const ColumnLabel = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLSpanElement>) => (
  <span className={cn(columnLabelClass, className)} {...props} />
);
ColumnLabel.displayName = 'ColumnLabel';

export {
  PageTitle,
  SubpageTitle,
  SectionTitle,
  SubsectionTitle,
  SmailTitle,
  Description,
  ScreenTitle,
  SectionLabel,
  ColumnLabel,
};
